import { AlertCircle, CheckCircle2, Star } from 'lucide-react'
import { formatPhone as formatAdminPhone } from '@/hooks/use-phone-formatter'
import { getFsnDeliveryAreaPreviewTotal as deliveryAreaPreviewTotal } from '@/lib/services/fsn-imports/fsn-imports.flow'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { EmptyState } from '@/components/patterns/empty-state'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { formatCurrency } from '@/lib/utils'
import type { FsnImportPlan } from '@/lib/services/fsn-imports/fsn-imports.types'
import { FsnCustomPagesWithContent } from './FsnCustomPagesWithContent'
import { BreakdownMetric, BreakdownRow, PreviewMetric } from './FsnImportMetrics'

type FsnImportPlanSections = FsnImportPlan['sections']

type FsnProductPreviewRow = NonNullable<FsnImportPlanSections['products']>['preview_rows'][number]

/** Location, delivery area, customer, review, and add-on rows are untyped records in the schema. */
type FsnGenericPreviewRow = NonNullable<FsnImportPlanSections['location']>['preview_rows'][number]

type FsnNotificationContactPreviewRow = NonNullable<
  FsnImportPlanSections['notification_contacts']
>['preview_rows'][number]

type FsnSocialLinkPreviewRow = NonNullable<
  FsnImportPlanSections['social_links']
>['preview_rows'][number]

type FsnReviewSummary = NonNullable<FsnImportPlanSections['reviews']>['summary']

interface NotificationContactPreview {
  value: string
  label: string
  enabled: boolean
}

interface SocialLinkGroup {
  key: string
  label: string
  values: string[]
}

const previewRowLimit = 10

const notificationContactChannels = [
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'text', label: 'Text' },
  { key: 'fax', label: 'Fax' },
]

const socialPlatformLabels: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
}

const reviewStatusKeys = ['published', 'pending', 'hidden', 'rejected'] as const

type ReviewStatus = (typeof reviewStatusKeys)[number]

const reviewStatusLabels: Record<ReviewStatus, string> = {
  published: 'Published',
  pending: 'Pending',
  hidden: 'Hidden',
  rejected: 'Rejected',
}

/**
 * Reads a field of an untyped preview row as display text. The API sends strings and numbers here;
 * anything else reads as the fallback.
 */
function previewText(value: unknown, fallback = ''): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }

  return fallback
}

function getNotificationContactValue(contact: FsnNotificationContactPreviewRow): string {
  if (contact.channel === 'email') {
    return contact.value
  }

  return formatAdminPhone(contact.value)
}

function getSocialPlatformLabel(platform: string): string {
  const normalizedPlatform = String(platform ?? '')
    .trim()
    .toLowerCase()
  const knownLabel = socialPlatformLabels[normalizedPlatform]

  if (knownLabel) {
    return knownLabel
  }

  return normalizedPlatform
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
}

function groupNotificationContacts(contacts: FsnNotificationContactPreviewRow[]) {
  return notificationContactChannels.map((channel) => {
    const valuesByContact = contacts
      .filter((contact) => contact.channel === channel.key)
      .reduce((values, contact) => {
        const value = getNotificationContactValue(contact)
        const label = String(contact.label ?? '').trim()

        if (!value) {
          return values
        }

        const existingContact = values.get(value)

        if (existingContact) {
          existingContact.enabled = existingContact.enabled || contact.enabled !== false

          if (!existingContact.label && label) {
            existingContact.label = label
          }

          return values
        }

        values.set(value, {
          value,
          label,
          enabled: contact.enabled !== false,
        })

        return values
      }, new Map<string, NotificationContactPreview>())

    return {
      ...channel,
      values: Array.from(valuesByContact.values()),
    }
  })
}

function groupSocialLinks(links: FsnSocialLinkPreviewRow[]): SocialLinkGroup[] {
  return links.reduce<SocialLinkGroup[]>((groups, link) => {
    const platform = String(link.platform ?? '').trim()
    const url = String(link.url ?? '').trim()

    if (!platform || !url) {
      return groups
    }

    const key = platform.toLowerCase()
    const existingGroup = groups.find((group) => group.key === key)

    if (existingGroup) {
      if (!existingGroup.values.includes(url)) {
        existingGroup.values.push(url)
      }

      return groups
    }

    return [
      ...groups,
      {
        key,
        label: getSocialPlatformLabel(platform),
        values: [url],
      },
    ]
  }, [])
}

export function ImportSummaryGrid({ summary }: { summary: Partial<FsnImportPlan['summary']> }) {
  const values = {
    products_ready: 0,
    location_ready: false,
    delivery_areas_ready: 0,
    customers_ready: 0,
    reviews_ready: 0,
    add_ons_ready: 0,
    contacts_ready: 0,
    social_links_ready: 0,
    logo_available: false,
    ...summary,
  }

  return (
    <div className="@container/fsn-summary grid gap-2 @2xl/fsn-summary:grid-cols-3 @5xl/fsn-summary:grid-cols-9">
      <PreviewMetric label="Products" value={values.products_ready} />
      <PreviewMetric label="Location" value={values.location_ready ? 'Yes' : 'No'} />
      <PreviewMetric label="Delivery" value={values.delivery_areas_ready} />
      <PreviewMetric label="Customers" value={values.customers_ready} />
      <PreviewMetric label="Reviews" value={values.reviews_ready} />
      <PreviewMetric label="Add-ons" value={values.add_ons_ready} />
      <PreviewMetric label="Contacts" value={values.contacts_ready} />
      <PreviewMetric label="Social links" value={values.social_links_ready} />
      <PreviewMetric label="Logo" value={values.logo_available ? 'Yes' : 'No'} />
    </div>
  )
}

function ProductPreviewList({
  rows = [],
  total = rows.length,
}: {
  rows?: FsnProductPreviewRow[]
  total?: number
}) {
  if (rows.length === 0) {
    return null
  }

  const visibleRows = rows.slice(0, previewRowLimit)
  const rowTotal = Math.max(Number(total ?? rows.length), rows.length)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <Typography variant="label" tone="muted">
          Product preview
        </Typography>
        {visibleRows.length < rowTotal ? (
          <Typography variant="label" tone="muted">
            {formatShowingCount(visibleRows.length, rowTotal)}
          </Typography>
        ) : null}
      </div>
      <div className="divide-y divide-border/70 border-y border-border/70">
        {visibleRows.map((row) => (
          <div
            key={row.source_key}
            className="grid gap-3 py-3 @4xl/fsn-preview:grid-cols-[minmax(0,1.2fr)_minmax(10rem,0.7fr)_minmax(0,1fr)] @4xl/fsn-preview:items-start"
          >
            <div className="min-w-0">
              <div className="break-words text-sm font-medium">{row.name}</div>
              <Typography variant="label" tone="muted" className="mt-1 block">
                {row.source_key}
              </Typography>
            </div>
            <div className="text-sm text-muted-foreground">{getProductPreviewPlacement(row)}</div>
            <div className="min-w-0 text-sm text-muted-foreground">
              {row.collections?.length > 0 ? row.collections.join(', ') : 'No current page'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function GenericPreviewList({
  title,
  rows = [],
  emptyLabel,
  total = rows.length,
}: {
  title: string
  rows?: FsnGenericPreviewRow[]
  emptyLabel: string
  total?: number
}) {
  if (rows.length === 0) {
    return <EmptyState size="compact" title={emptyLabel} />
  }

  const visibleRows = rows.slice(0, previewRowLimit)
  const rowTotal = Math.max(Number(total ?? rows.length), rows.length)

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/70 px-3 py-2">
        <Typography variant="label" tone="muted">
          {title}
        </Typography>
        {visibleRows.length < rowTotal ? (
          <Typography variant="label" tone="muted">
            {formatShowingCount(visibleRows.length, rowTotal)}
          </Typography>
        ) : null}
      </div>
      <div className="divide-y divide-border/70">
        {visibleRows.map((row, index) => (
          <div
            key={`${previewText(row.label ?? row.name ?? row.type, 'row')}-${index}`}
            className="grid gap-2 px-3 py-3 @3xl/fsn-preview:grid-cols-[10rem_minmax(0,1fr)] @3xl/fsn-step:grid-cols-[10rem_minmax(0,1fr)]"
          >
            <h4 className="text-sm font-medium text-foreground">
              {previewText(row.label ?? row.name ?? row.type, 'Item')}
            </h4>
            <div className="min-w-0 break-words text-sm text-muted-foreground">
              {row.value !== undefined && row.value !== null
                ? formatGenericPreviewRowValue(row)
                : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function formatPreviewValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(formatPreviewValue).join(', ')
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  return previewText(value)
}

function formatGenericPreviewRowValue(row: FsnGenericPreviewRow): string {
  if (previewText(row.label).trim().toLowerCase() === 'phone') {
    return formatAdminPhone(previewText(row.value))
  }

  return formatPreviewValue(row.value)
}

function formatShowingCount(visible: number, total: number): string {
  return `Showing ${visible} out of ${Math.max(total ?? 0, visible)} total`
}

export function CustomerPreviewList({
  customers = [],
  selected,
  total = customers.length,
}: {
  customers?: FsnGenericPreviewRow[]
  selected: boolean
  total?: number
}) {
  if (!selected) {
    return null
  }

  if (customers.length === 0) {
    return <EmptyState size="compact" title="No customers found for this FSN account." />
  }

  const visibleCustomers = customers.slice(0, previewRowLimit)

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/70 px-3 py-2">
        <Typography variant="label" tone="muted">
          Customers to import
        </Typography>
        <Typography variant="label" tone="muted">
          {formatShowingCount(visibleCustomers.length, total)}
        </Typography>
      </div>
      <div className="divide-y divide-border/70">
        {visibleCustomers.map((customer, index) => (
          <div
            key={`${previewText(customer.email ?? customer.phone ?? customer.name)}-${index}`}
            className="grid gap-2 px-3 py-3 @3xl/fsn-preview:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] @3xl/fsn-step:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
          >
            <div className="min-w-0">
              <div className="break-words text-sm font-medium">
                {previewText(customer.name) || 'FSN customer'}
              </div>
              <Typography variant="label" tone="muted" className="mt-1 block">
                {[previewText(customer.city), previewText(customer.state)]
                  .filter(Boolean)
                  .join(', ') || 'No address preview'}
              </Typography>
            </div>
            <div className="min-w-0 text-sm text-muted-foreground">
              {customer.email ? (
                <div className="break-words">{previewText(customer.email)}</div>
              ) : null}
              {customer.phone ? <div>{formatAdminPhone(previewText(customer.phone))}</div> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function reviewStatusCount(summary: Partial<FsnReviewSummary>, status: ReviewStatus): number {
  return Number(summary?.[status] ?? 0)
}

function visibleReviewStatusCounts(reviews: FsnGenericPreviewRow[]): Record<string, number> {
  return reviews.reduce<Record<string, number>>((counts, review) => {
    const status = previewText(review.status, 'hidden')
    counts[status] = (counts[status] ?? 0) + 1

    return counts
  }, {})
}

export function ReviewPreviewList({
  reviews = [],
  selected,
  summary = {},
}: {
  reviews?: FsnGenericPreviewRow[]
  selected: boolean
  summary?: Partial<FsnReviewSummary>
}) {
  if (!selected) {
    return null
  }

  if (reviews.length === 0) {
    return <EmptyState size="compact" title="No reviews found for this FSN account." />
  }

  const visibleReviews = reviews.slice(0, previewRowLimit)
  const total = Number(summary?.total ?? reviews.length)
  const visibleStatusCounts = visibleReviewStatusCounts(visibleReviews)
  const statusRows = reviewStatusKeys
    .map((status) => ({
      status,
      label: reviewStatusLabels[status],
      total: reviewStatusCount(summary, status),
      visible: visibleStatusCounts[status] ?? 0,
    }))
    .filter((row) => row.total > 0 || row.visible > 0)

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="space-y-2 border-b border-border/70 px-3 py-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <Typography variant="label" tone="muted">
            Reviews to import
          </Typography>
          <Typography variant="label" tone="muted">
            {formatShowingCount(visibleReviews.length, total)}
          </Typography>
        </div>
        {statusRows.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {statusRows.map((row) => (
              <Badge key={row.status} variant="secondary">
                {row.label}: showing {row.visible} out of {row.total}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
      <div className="divide-y divide-border/70">
        {visibleReviews.map((review, index) => (
          <div
            key={`${previewText(review.reviewer_name, 'review')}-${index}`}
            className="px-3 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">
                {previewText(review.reviewer_name) || 'FSN customer'}
              </span>
              {review.rating ? (
                <Badge variant="secondary">
                  <Star className="size-3" />
                  {previewText(review.rating)}
                </Badge>
              ) : null}
              <Badge variant={review.status === 'published' ? 'success' : 'secondary'}>
                {previewText(review.status)}
              </Badge>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{previewText(review.body)}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

export function AddOnPreviewList({
  addOns = [],
  selected,
  total = addOns.length,
}: {
  addOns?: FsnGenericPreviewRow[]
  selected: boolean
  total?: number
}) {
  if (!selected) {
    return null
  }

  if (addOns.length === 0) {
    return <EmptyState size="compact" title="No add-ons found for this FSN client." />
  }

  const visibleAddOns = addOns.slice(0, previewRowLimit)

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/70 px-3 py-2">
        <Typography variant="label" tone="muted">
          Add-ons to import
        </Typography>
        <Typography variant="label" tone="muted">
          {formatShowingCount(visibleAddOns.length, total)}
        </Typography>
      </div>
      <div className="divide-y divide-border/70">
        {visibleAddOns.map((addOn, index) => (
          <div
            key={`${previewText(addOn.name)}-${index}`}
            className="grid gap-3 px-3 py-3 @3xl/fsn-preview:grid-cols-[3.5rem_minmax(0,1fr)_minmax(9rem,auto)] @3xl/fsn-preview:items-center @3xl/fsn-step:grid-cols-[3.5rem_minmax(0,1fr)_minmax(9rem,auto)] @3xl/fsn-step:items-center"
          >
            <div className="size-14 overflow-hidden rounded-md border border-border/70 bg-muted/20">
              {addOn.image_url ? (
                <img
                  src={previewText(addOn.image_url)}
                  alt={previewText(addOn.name) || 'FSN add-on'}
                  className="size-full object-cover"
                  loading="lazy"
                />
              ) : null}
            </div>
            <div className="min-w-0 break-words text-sm font-medium">{previewText(addOn.name)}</div>
            <div className="text-sm text-muted-foreground @3xl/fsn-preview:text-right @3xl/fsn-step:text-right">
              {(Array.isArray(addOn.prices) ? addOn.prices : [])
                .map((price) => formatCurrency(price))
                .join(', ')}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function getProductPreviewPlacement(row: FsnProductPreviewRow): string {
  if (row.has_active_page_placement) {
    return 'Current page'
  }

  return row.product_source === 'FSN master' ? 'Hidden FSN product' : 'Hidden custom product'
}

export function PreviewPanel({ plan }: { plan: FsnImportPlan | null }) {
  if (!plan) {
    return (
      <EmptyState
        size="compact"
        title="Preview"
        description="Counts, warnings, and sample products will appear here before anything is imported."
        action={<Badge variant="muted">Not previewed</Badge>}
      />
    )
  }

  if (plan.status === 'account_required') {
    return (
      <Alert variant="warning">
        <AlertCircle className="size-4" />
        <AlertTitle>Choose a website account</AlertTitle>
        <AlertDescription>
          This FSN client has multiple website accounts. Select the current storefront account and
          preview again.
        </AlertDescription>
      </Alert>
    )
  }

  const productSummary = plan.sections.products?.summary
  const locationSection = plan.sections.location
  const deliveryAreaSection = plan.sections.delivery_areas
  const customerSection = plan.sections.customers
  const reviewSection = plan.sections.reviews
  const addOnSection = plan.sections.add_ons
  const contactSection = plan.sections.notification_contacts
  const contactRows = contactSection?.preview_rows ?? []
  const socialSection = plan.sections.social_links
  const socialRows = plan.sections.social_links?.preview_rows ?? []
  const previewRows = plan.sections.products?.preview_rows ?? []
  const deliveryAreaTotal = deliveryAreaPreviewTotal(deliveryAreaSection?.summary)
  const distancePreviewRows = deliveryAreaSection?.distance_preview_rows ?? []
  const distancePreviewTotal = Math.max(
    Number(deliveryAreaSection?.distance_preview_total ?? 0),
    distancePreviewRows.length
  )

  return (
    <section className="@container/fsn-preview space-y-6">
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Preview ready</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {plan.account
                ? `Website account ${plan.account.username}${plan.account.sitename ? ` / ${plan.account.sitename}` : ''}`
                : 'FSN client and network data'}
            </p>
          </div>
          <Badge variant="success">
            <CheckCircle2 className="size-3.5" />
            Ready
          </Badge>
        </div>
      </div>
      <div className="space-y-6">
        <ImportSummaryGrid summary={plan.summary} />

        {productSummary ? (
          <div className="space-y-3">
            <Typography variant="label" tone="muted">
              Product breakdown
            </Typography>
            <div className="grid gap-2 @3xl/fsn-preview:grid-cols-2 @6xl/fsn-preview:grid-cols-4">
              <BreakdownMetric
                label="FSN page products"
                value={productSummary.fsn_current_page_products}
              />
              <BreakdownMetric
                label="FSN hidden products"
                value={productSummary.fsn_off_page_products}
                tone={productSummary.fsn_off_page_products > 0 ? 'warning' : 'default'}
              />
              <BreakdownMetric
                label="Custom page products"
                value={productSummary.custom_current_page_products}
              />
              <BreakdownMetric
                label="Custom hidden products"
                value={productSummary.custom_off_page_products}
              />
            </div>
          </div>
        ) : null}

        <FsnCustomPagesWithContent
          pages={plan.sections.products?.custom_pages_with_content ?? []}
        />

        <div className="grid gap-5 @5xl/fsn-preview:grid-cols-2 @5xl/fsn-preview:items-start">
          <div className="contents @5xl/fsn-preview:block @5xl/fsn-preview:space-y-5">
            <div className="order-1 space-y-3 @5xl/fsn-preview:order-none">
              <Typography variant="label" tone="muted">
                Fulfillment
              </Typography>
              {locationSection?.selected ? (
                <GenericPreviewList
                  title="Location details"
                  rows={locationSection.preview_rows ?? []}
                  emptyLabel="No location details selected."
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No location details selected.
                </Typography>
              )}
              {deliveryAreaSection?.selected ? (
                <>
                  <GenericPreviewList
                    title="Delivery city and ZIP fees"
                    rows={deliveryAreaSection.preview_rows ?? []}
                    emptyLabel="No delivery city or ZIP fees selected."
                    total={deliveryAreaTotal}
                  />
                  <GenericPreviewList
                    title="Driving-distance fees"
                    rows={distancePreviewRows}
                    emptyLabel="No driving-distance fees selected."
                    total={distancePreviewTotal}
                  />
                </>
              ) : (
                <Typography variant="supporting" className="block">
                  No delivery fees selected.
                </Typography>
              )}
            </div>

            <div className="order-3 space-y-3 @5xl/fsn-preview:order-none">
              <Typography variant="label" tone="muted">
                Add-ons
              </Typography>
              {addOnSection?.selected ? (
                <AddOnPreviewList
                  addOns={addOnSection.preview_rows ?? []}
                  selected
                  total={addOnSection.summary?.total}
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No add-ons selected.
                </Typography>
              )}
            </div>

            <div className="order-5 space-y-3 @5xl/fsn-preview:order-none">
              <Typography variant="label" tone="muted">
                Website design
              </Typography>
              <div className="grid gap-2">
                <BreakdownRow
                  label="Logo available"
                  value={plan.summary.logo_available ? 'Yes' : 'No'}
                />
              </div>
              {socialSection?.selected ? (
                <SocialLinkPreviewList
                  links={socialRows}
                  selected
                  total={socialSection.summary?.total}
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No social links selected.
                </Typography>
              )}
            </div>
          </div>

          <div className="contents @5xl/fsn-preview:block @5xl/fsn-preview:space-y-5">
            <div className="order-2 space-y-3 @5xl/fsn-preview:order-none">
              <Typography variant="label" tone="muted">
                Customers and reviews
              </Typography>
              {customerSection?.selected ? (
                <CustomerPreviewList
                  customers={customerSection.preview_rows ?? []}
                  selected
                  total={customerSection.summary?.total}
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No customers selected.
                </Typography>
              )}
              {reviewSection?.selected ? (
                <ReviewPreviewList
                  reviews={reviewSection.preview_rows ?? []}
                  selected
                  summary={reviewSection.summary}
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No reviews selected.
                </Typography>
              )}
            </div>

            <div className="order-4 space-y-3 @5xl/fsn-preview:order-none">
              {contactSection?.selected ? (
                <NotificationContactPreviewList
                  contacts={contactRows}
                  selected
                  total={contactSection.summary?.total}
                  enabledTotal={contactSection.summary?.enabled}
                  disabledTotal={contactSection.summary?.disabled}
                />
              ) : (
                <Typography variant="supporting" className="block">
                  No notification contacts selected.
                </Typography>
              )}
            </div>
          </div>
        </div>

        <ProductPreviewList rows={previewRows} total={productSummary?.ready} />
      </div>
    </section>
  )
}

export function NotificationContactPreviewList({
  contacts,
  selected,
  total = contacts.length,
  enabledTotal = 0,
  disabledTotal = 0,
}: {
  contacts: FsnNotificationContactPreviewRow[]
  selected: boolean
  total?: number
  enabledTotal?: number
  disabledTotal?: number
}) {
  if (!selected) {
    return null
  }

  if (contacts.length === 0) {
    return <EmptyState size="compact" title="No notification contacts found for this FSN client." />
  }

  const visibleContacts = contacts.slice(0, previewRowLimit)
  const rowTotal = Math.max(Number(total ?? contacts.length), contacts.length)
  const groupedContacts = groupNotificationContacts(visibleContacts).filter(
    (channel) => channel.values.length > 0
  )

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/70 px-3 py-2">
        <Typography variant="label" tone="muted">
          Notification contacts to import
        </Typography>
        <Typography variant="label" tone="muted">
          {[
            visibleContacts.length < rowTotal
              ? formatShowingCount(visibleContacts.length, rowTotal)
              : null,
            `${Number(enabledTotal ?? 0)} enabled`,
            `${Number(disabledTotal ?? 0)} disabled`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      </div>
      <div className="divide-y divide-border/70">
        {groupedContacts.map((channel) => (
          <div
            key={channel.key}
            className="grid gap-2 px-3 py-3 @3xl/fsn-preview:grid-cols-[7rem_minmax(0,1fr)] @3xl/fsn-step:grid-cols-[7rem_minmax(0,1fr)]"
          >
            <h4 className="text-sm font-medium text-foreground">{channel.label}</h4>
            <div className="min-w-0 space-y-1">
              {channel.values.map((contact) => {
                const label =
                  contact.label?.toLowerCase() === channel.label.toLowerCase() ? '' : contact.label

                return (
                  <div
                    key={`${channel.key}-${contact.value}`}
                    className="flex flex-wrap items-center gap-2 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block break-words font-medium">{contact.value}</span>
                      {label ? (
                        <Typography variant="label" tone="muted" className="block">
                          {label}
                        </Typography>
                      ) : null}
                    </span>
                    <Badge variant={contact.enabled ? 'success' : 'secondary'}>
                      {contact.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function SocialLinkPreviewList({
  links = [],
  selected,
  total = links.length,
}: {
  links?: FsnSocialLinkPreviewRow[]
  selected: boolean
  total?: number
}) {
  if (!selected) {
    return null
  }

  const visibleLinks = links.slice(0, previewRowLimit)
  const rowTotal = Math.max(Number(total ?? links.length), links.length)
  const groupedLinks = groupSocialLinks(visibleLinks)

  if (groupedLinks.length === 0) {
    return (
      <EmptyState size="compact" title="No supported social links found for this FSN client." />
    )
  }

  return (
    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border/70 px-3 py-2">
        <Typography variant="label" tone="muted">
          Social links to import
        </Typography>
        {visibleLinks.length < rowTotal ? (
          <Typography variant="label" tone="muted">
            {formatShowingCount(visibleLinks.length, rowTotal)}
          </Typography>
        ) : null}
      </div>
      <div className="divide-y divide-border/70">
        {groupedLinks.map((platform) => (
          <div
            key={platform.key}
            className="grid gap-2 px-3 py-3 @3xl/fsn-preview:grid-cols-[7rem_minmax(0,1fr)] @3xl/fsn-step:grid-cols-[7rem_minmax(0,1fr)]"
          >
            <h4 className="text-sm font-medium text-foreground">{platform.label}</h4>
            <div className="min-w-0 space-y-1">
              {platform.values.map((url) => (
                <a
                  key={`${platform.key}-${url}`}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="block break-words text-sm font-medium text-foreground underline-offset-2 hover:underline [overflow-wrap:anywhere]"
                >
                  {url}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
